import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { changeRequestsApi, CR_TYPE_META, CR_STATUS_META } from "@/api/changeRequests";
import { fmtNum } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime } from "@/utils/date";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ClipboardText,
  Clock,
  CheckCircle,
  XCircle,
  Note,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const TABS = [
  { key: "pending",  label: "قيد الانتظار" },
  { key: "approved", label: "معتمدة" },
  { key: "rejected", label: "مرفوضة" },
];

/**
 * Company self-service: view own change requests + statuses.
 * Read-only counterpart of /admin/change-requests.
 */
export default function CompanyMyRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Admins and drivers don't have change requests → redirect.
  useEffect(() => {
    if (user && (user.is_admin || user.role === "driver")) {
      navigate(user.is_admin ? "/admin/change-requests" : "/", { replace: true });
    }
  }, [user, navigate]);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const data = await changeRequestsApi.mine(status);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <ClipboardText size={26} weight="duotone" className="text-primary" />
              طلباتي
            </h1>
            <p className="text-white/50 text-sm mt-1">
              تابع حالة طلباتك المُرسَلة للإدارة —{" "}
              <span className="text-primary font-bold num">{fmtNum(items.length)}</span> طلب
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(tab)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 text-xs font-bold disabled:opacity-50"
            data-testid="my-cr-refresh"
          >
            <ArrowsClockwise size={13} weight="bold" /> تحديث
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-primary/15 pb-2 flex-wrap">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`my-cr-tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white/5 border-white/15 text-white/60 hover:bg-white/10"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-card border border-primary/15 p-10 text-center text-white/60">
            {tab === "pending"
              ? "لا توجد طلبات قيد الانتظار."
              : tab === "approved"
                ? "لم يتم اعتماد أي من طلباتك بعد."
                : "لا توجد طلبات مرفوضة."}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((cr) => <MyReqCard key={cr.id} cr={cr} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function MyReqCard({ cr }) {
  const key = `${cr.target_type}:${cr.action}`;
  const meta = CR_TYPE_META[key] || { label: key, color: "#C9A961", bg: "rgba(201,169,97,0.15)", border: "rgba(201,169,97,0.4)" };
  const status = CR_STATUS_META[cr.status] || CR_STATUS_META.pending;
  const iso = cr.created_at;
  const statusIcon = cr.status === "approved"
    ? <CheckCircle size={12} weight="fill" />
    : cr.status === "rejected"
      ? <XCircle size={12} weight="fill" />
      : <Clock size={12} weight="fill" />;

  return (
    <div
      className="rounded-2xl bg-card border border-primary/15 p-4 md:p-5 fadeup"
      data-testid={`my-cr-card-${cr.id}`}
    >
      <div className="flex items-center flex-wrap gap-2 mb-2">
        <span
          className="text-[11px] font-bold px-2.5 py-1 rounded-full border"
          style={{ background: meta.bg, borderColor: meta.border, color: meta.color }}
        >
          {meta.label}
        </span>
        <span
          className="text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1"
          style={{ background: `${status.color}15`, color: status.color, border: `1px solid ${status.color}50` }}
        >
          {statusIcon} {status.label}
        </span>
        {iso && (
          <span className="text-white/45 text-[11px] num inline-flex items-center gap-1 mr-auto" dir="ltr">
            <Clock size={11} /> {fmtDate(iso)} · {fmtTime(iso)}
          </span>
        )}
      </div>

      <div className="text-white/85 text-sm">{cr.summary || "—"}</div>

      {cr.admin_note && cr.status !== "pending" && (
        <div className="mt-3 pt-3 border-t border-primary/10 text-xs text-white/70 inline-flex items-start gap-1">
          <Note size={12} weight="duotone" className="text-primary/60 shrink-0 mt-0.5" />
          <span><span className="text-primary font-bold">ملاحظة الإدارة:</span> {cr.admin_note}</span>
        </div>
      )}
    </div>
  );
}
